import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { TrendingUp, Users, CreditCard, MousePointerClick, Bell, Eye } from "lucide-react";

type DailyPoint = { date: string; count: number };

type Range = 7 | 30 | 90;

function startOfRange(days: Range): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - (days - 1));
  return d;
}

function bucketByDay(rows: { created_at: string }[], days: Range): DailyPoint[] {
  const start = startOfRange(days);
  const buckets = new Map<string, number>();
  for (let i = 0; i < days; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    buckets.set(d.toISOString().slice(0, 10), 0);
  }
  for (const r of rows) {
    const key = new Date(r.created_at).toISOString().slice(0, 10);
    if (buckets.has(key)) buckets.set(key, (buckets.get(key) || 0) + 1);
  }
  return Array.from(buckets.entries()).map(([date, count]) => ({
    date: date.slice(5),
    count,
  }));
}

export function AnalyticsTab() {
  const [range, setRange] = useState<Range>(30);
  const [loading, setLoading] = useState(true);
  const [signups, setSignups] = useState<DailyPoint[]>([]);
  const [conversions, setConversions] = useState<DailyPoint[]>([]);
  const [watchesByDay, setWatchesByDay] = useState<DailyPoint[]>([]);
  const [alertsByDay, setAlertsByDay] = useState<DailyPoint[]>([]);
  const [totals, setTotals] = useState({
    totalUsers: 0,
    totalLicenses: 0,
    leadsInRange: 0,
    activeUsers: 0,
    signupsInRange: 0,
    conversionsInRange: 0,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const start = startOfRange(range).toISOString();

      const [
        prefsRes,
        licensesRes,
        leadsCountRes,
        watchesRes,
        alertsRes,
        actionsRes,
        usersTotalRes,
        licensesTotalRes,
      ] = await Promise.all([
        supabase
          .from("user_preferences")
          .select("created_at")
          .gte("created_at", start)
          .order("created_at", { ascending: true }),
        supabase
          .from("licenses")
          .select("created_at")
          .gte("created_at", start)
          .eq("status", "active")
          .order("created_at", { ascending: true }),
        supabase
          .from("leads")
          .select("id", { count: "exact", head: true })
          .gte("created_at", start),
        supabase
          .from("watches")
          .select("created_at")
          .gte("created_at", start),
        supabase
          .from("trend_alert_rules")
          .select("created_at")
          .gte("created_at", start),
        supabase
          .from("user_trend_actions")
          .select("user_id")
          .gte("created_at", start),
        supabase.from("user_preferences").select("id", { count: "exact", head: true }),
        supabase
          .from("licenses")
          .select("id", { count: "exact", head: true })
          .eq("status", "active"),
      ]);

      if (cancelled) return;

      const signupRows = (prefsRes.data as { created_at: string }[]) ?? [];
      const licenseRows = (licensesRes.data as { created_at: string }[]) ?? [];
      const watchRows = (watchesRes.data as { created_at: string }[]) ?? [];
      const alertRows = (alertsRes.data as { created_at: string }[]) ?? [];
      const actionRows = (actionsRes.data as { user_id: string }[]) ?? [];

      setSignups(bucketByDay(signupRows, range));
      setConversions(bucketByDay(licenseRows, range));
      setWatchesByDay(bucketByDay(watchRows, range));
      setAlertsByDay(bucketByDay(alertRows, range));

      const activeUsers = new Set(actionRows.map((a) => a.user_id)).size;

      setTotals({
        totalUsers: usersTotalRes.count ?? 0,
        totalLicenses: licensesTotalRes.count ?? 0,
        leadsInRange: leadsCountRes.count ?? 0,
        activeUsers,
        signupsInRange: signupRows.length,
        conversionsInRange: licenseRows.length,
      });
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [range]);

  const conversionRate = useMemo(() => {
    if (totals.signupsInRange === 0) return 0;
    return Math.round((totals.conversionsInRange / totals.signupsInRange) * 1000) / 10;
  }, [totals.signupsInRange, totals.conversionsInRange]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-xl font-bold">Product analytics</h2>
          <p className="text-sm text-muted-foreground">
            Live metrics pulled from your own database. For traffic and page views, see{" "}
            <a
              href="https://plausible.io/trendsdiscovery.com"
              target="_blank"
              rel="noreferrer"
              className="underline text-primary"
            >
              Plausible dashboard ↗
            </a>
            .
          </p>
        </div>
        <div className="flex gap-1 rounded-lg border border-border p-1">
          {([7, 30, 90] as Range[]).map((r) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              className={`px-3 py-1.5 font-mono text-xs uppercase rounded-md transition-colors ${
                range === r
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {r}d
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <MetricCard
          icon={Users}
          label="Sign-ups"
          value={totals.signupsInRange}
          sub={`${totals.totalUsers} total`}
        />
        <MetricCard
          icon={CreditCard}
          label="Conversions"
          value={totals.conversionsInRange}
          sub={`${totals.totalLicenses} active`}
        />
        <MetricCard
          icon={TrendingUp}
          label="Conv. rate"
          value={`${conversionRate}%`}
          sub="signup → paid"
        />
        <MetricCard icon={MousePointerClick} label="Leads" value={totals.leadsInRange} sub="captured" />
        <MetricCard icon={Eye} label="Active users" value={totals.activeUsers} sub="took action" />
        <MetricCard
          icon={Bell}
          label="Watches + alerts"
          value={watchesByDay.reduce((a, b) => a + b.count, 0) + alertsByDay.reduce((a, b) => a + b.count, 0)}
          sub="created"
        />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <ChartCard title="Sign-ups per day" loading={loading} data={signups} color="hsl(var(--primary))" />
        <ChartCard
          title="Paid conversions per day"
          loading={loading}
          data={conversions}
          color="hsl(var(--accent))"
        />
        <ChartCard
          title="New watches per day"
          loading={loading}
          data={watchesByDay}
          color="hsl(var(--primary))"
        />
        <ChartCard
          title="New alert rules per day"
          loading={loading}
          data={alertsByDay}
          color="hsl(var(--accent))"
        />
      </div>

      <div className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">
        <div className="font-mono text-xs text-primary mb-2">// EVENT TRACKING</div>
        Page views and custom events (sign-up, sign-in, checkout started, checkout success,
        onboarding completed, watch added, alert rule created, password reset) stream to
        Plausible in real time. No cookies, no consent banner required.
      </div>
    </div>
  );
}

function MetricCard({
  icon: Icon,
  label,
  value,
  sub,
}: {
  icon: typeof Users;
  label: string;
  value: number | string;
  sub?: string;
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center gap-2 text-muted-foreground">
        <Icon className="size-3.5" />
        <span className="font-mono text-[10px] uppercase">{label}</span>
      </div>
      <div className="mt-2 font-display text-2xl font-bold">{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function ChartCard({
  title,
  data,
  loading,
  color,
}: {
  title: string;
  data: DailyPoint[];
  loading: boolean;
  color: string;
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display font-semibold">{title}</h3>
        <span className="font-mono text-[10px] text-muted-foreground">
          {loading ? "loading…" : `total ${data.reduce((a, b) => a + b.count, 0)}`}
        </span>
      </div>
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
            <defs>
              <linearGradient id={`g-${title}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.4} />
                <stop offset="100%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
            <YAxis tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
            <RTooltip
              contentStyle={{
                background: "hsl(var(--card))",
                border: "1px solid hsl(var(--border))",
                fontSize: 12,
              }}
            />
            <Area type="monotone" dataKey="count" stroke={color} fill={`url(#g-${title})`} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
